import { Place as SiteIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Link,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { monospaceStyle } from "../../theme";
import { EntityRef } from "../../types/productPlane";
import {
  ChainCardType,
  ChainFacility,
  ChainMaker,
  ChainPair,
  MakerStanding,
} from "../../types/valueChain";
import { exploreAddress } from "../CategoryDetail/CategoryHead";
import EntityLink, { entityLinkText } from "../Detail/EntityLink";
import { codeWords } from "../Detail/format";

// Which makers the table lists
export type MakerScope = "all" | "chain" | "anywhere";

// The standings each scope lists
const SCOPE_STANDINGS: Record<MakerScope, readonly MakerStanding[]> = {
  all: ["proven", "proven_elsewhere", "capable"],
  chain: ["proven"],
  anywhere: ["proven", "proven_elsewhere"],
};

// The standings in the order the read gives them
const STANDING_ORDER: Record<MakerStanding, number> = {
  proven: 0,
  proven_elsewhere: 1,
  capable: 2,
};

// What the table is sorted by
export type MakerSort = "products" | "standing" | "name";

const SORTS: readonly MakerSort[] = ["products", "standing", "name"];

// Rows the table shows at a time
export const MAKERS_PAGE_SIZE = 7;

// The country filter's value for every country
const ANY_COUNTRY = "";

// What a cell reads where the read holds nothing
const NOTHING = "—";

const makerName = (maker: ChainMaker): string =>
  entityLinkText(maker.organization.type, maker.organization.name);

const byProducts = (a: ChainMaker, b: ChainMaker): number =>
  b.product_count - a.product_count;

const byStanding = (a: ChainMaker, b: ChainMaker): number =>
  STANDING_ORDER[a.standing] - STANDING_ORDER[b.standing];

const byName = (a: ChainMaker, b: ChainMaker): number =>
  makerName(a).localeCompare(makerName(b));

const COMPARE: Record<MakerSort, (a: ChainMaker, b: ChainMaker) => number> = {
  products: (a, b) => byProducts(a, b) || byStanding(a, b) || byName(a, b),
  standing: (a, b) => byStanding(a, b) || byProducts(a, b) || byName(a, b),
  name: byName,
};

/**
 * The host products a maker's proving product is stated inside, among the
 * stated pairs on record: the proving pair's host first, each once. Empty for
 * a maker with no proving pair.
 */
export const provenHosts = (
  maker: ChainMaker,
  pairs: readonly ChainPair[],
): EntityRef[] => {
  const proof = maker.proven_by;
  if (!proof) {
    return [];
  }
  const hosts = new Map<string, EntityRef>([[proof.host.id, proof.host]]);
  for (const pair of pairs) {
    if (pair.part.id === proof.part.id && !hosts.has(pair.host.id)) {
      hosts.set(pair.host.id, pair.host);
    }
  }
  return Array.from(hosts.values());
};

const numberCell = {
  align: "right" as const,
  sx: { ...monospaceStyle, whiteSpace: "nowrap" },
};

// A small filter chip; the one chosen is outlined in the text colour
const filterChipSx = (selected: boolean) => ({
  height: 20,
  fontSize: "0.71875rem",
  "& .MuiChip-label": { px: 0.875 },
  ...(selected && { borderColor: "text.primary", color: "text.primary" }),
});

// The opening mark of a menu, beside its name
const MenuMark: React.FC = () => (
  <Box component="span" aria-hidden sx={{ ml: 0.5 }}>
    ▾
  </Box>
);

// A caption beside the content of a cell
const CellNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ typography: "caption", color: "text.disabled" }}>
    {children}
  </Box>
);

// The link of a production site as a pin; the name is its tooltip
const SiteLink: React.FC<{ site: ChainFacility }> = ({ site }) => (
  <Tooltip
    title={entityLinkText(site.facility.type, site.facility.name)}
    describeChild
  >
    <Box
      component="span"
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        ml: 0.75,
        verticalAlign: "middle",
      }}
    >
      <SiteIcon aria-hidden sx={{ fontSize: 14, color: "success.main" }} />
      {/* The name is read out, not shown: the link covers the pin */}
      <EntityLink
        id={site.facility.id}
        type={site.facility.type}
        name={site.facility.name}
        sx={{
          fontSize: 0,
          "&::after": { content: '""', position: "absolute", inset: 0 },
        }}
      />
    </Box>
  </Tooltip>
);

// Where a maker is proven: the host product of its proving pair, the count of
// the further hosts on record, or another chain
const ProvenIn: React.FC<{ maker: ChainMaker; pairs: readonly ChainPair[] }> = ({
  maker,
  pairs,
}) => {
  if (!maker.proven_by || maker.standing === "capable") {
    return <Box component="span" sx={{ color: "text.disabled" }}>{NOTHING}</Box>;
  }
  const host = maker.proven_by.host;
  const more =
    maker.standing === "proven" ? provenHosts(maker, pairs).length - 1 : 0;
  return (
    <>
      <EntityLink
        id={host.id}
        type={host.type}
        name={host.name}
        sx={{ color: "accent.main", textDecorationColor: "currentcolor" }}
      />
      {maker.standing === "proven_elsewhere" ? (
        <>
          {" "}
          <CellNote>other chain</CellNote>
        </>
      ) : (
        more > 0 && (
          <>
            {" "}
            <CellNote>{`+${more}`}</CellNote>
          </>
        )
      )}
    </>
  );
};

interface KindMakersProps {
  makers: ChainMaker[];
  // The category the makers' products are compared in, in Explore
  categoryId: string;
  // What the makers make: a kind or a class
  type: ChainCardType;
  // The stated pairs on record with the products of the kind or the class
  // as parts, of the same chain as the makers
  pairs?: readonly ChainPair[];
}

/**
 * The makers of a kind or a class as a table: the organization with its role,
 * its country with the pins of its production sites, where it is proven, its
 * products and the articles behind the proving pair. Filtered to the makers
 * proven in this chain or proven anywhere, and by country; sorted, a page at
 * a time; and the step into comparing them.
 */
const KindMakers: React.FC<KindMakersProps> = ({
  makers,
  categoryId,
  type,
  pairs = [],
}) => {
  const [scope, setScope] = useState<MakerScope>("all");
  const [country, setCountry] = useState<string>(ANY_COUNTRY);
  const [sort, setSort] = useState<MakerSort>("products");
  const [pageIndex, setPageIndex] = useState(0);
  const [countryMenu, setCountryMenu] = useState<HTMLElement | null>(null);
  const [sortMenu, setSortMenu] = useState<HTMLElement | null>(null);

  const rows = makers;
  const countries = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((maker) => maker.country)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort(),
    [rows],
  );
  const scopeCount = (value: MakerScope): number =>
    rows.filter((maker) => SCOPE_STANDINGS[value].includes(maker.standing))
      .length;

  const shown = useMemo(
    () =>
      rows
        .filter(
          (maker) =>
            SCOPE_STANDINGS[scope].includes(maker.standing) &&
            (country === ANY_COUNTRY || maker.country === country),
        )
        .sort(COMPARE[sort]),
    [rows, scope, country, sort],
  );

  // Another filter or another order starts at the first page
  const pages = Math.max(1, Math.ceil(shown.length / MAKERS_PAGE_SIZE));
  const at = Math.min(pageIndex, pages - 1);
  const first = at * MAKERS_PAGE_SIZE;
  const page = shown.slice(first, first + MAKERS_PAGE_SIZE);
  const pickScope = (value: MakerScope) => {
    setScope(value);
    setPageIndex(0);
  };
  const pickCountry = (value: string) => {
    setCountry(value);
    setPageIndex(0);
    setCountryMenu(null);
  };
  const pickSort = (value: MakerSort) => {
    setSort(value);
    setPageIndex(0);
    setSortMenu(null);
  };

  const scopeChip = (value: MakerScope, label: string, withCount: boolean) => (
    <Chip
      size="small"
      variant="outlined"
      clickable
      aria-pressed={scope === value}
      onClick={() => pickScope(value)}
      sx={filterChipSx(scope === value)}
      label={
        <>
          {label}
          {withCount && (
            <>
              {" "}
              <Box component="span" sx={monospaceStyle}>
                {scopeCount(value)}
              </Box>
            </>
          )}
        </>
      }
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
        }}
      >
        <Box
          role="group"
          aria-label="Makers shown"
          sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
        >
          {scopeChip("all", "All", false)}
          {scopeChip("chain", "Proven in this chain", true)}
          {scopeChip("anywhere", "Proven anywhere", true)}
        </Box>
        <Chip
          size="small"
          variant="outlined"
          clickable
          disabled={countries.length === 0}
          aria-haspopup="menu"
          aria-expanded={countryMenu !== null}
          onClick={(event) => setCountryMenu(event.currentTarget)}
          sx={filterChipSx(country !== ANY_COUNTRY)}
          label={
            <>
              {country === ANY_COUNTRY ? "Country" : `Country · ${country}`}
              <MenuMark />
            </>
          }
        />
        <Menu
          anchorEl={countryMenu}
          open={countryMenu !== null}
          onClose={() => setCountryMenu(null)}
        >
          <MenuItem
            dense
            selected={country === ANY_COUNTRY}
            onClick={() => pickCountry(ANY_COUNTRY)}
          >
            Every country
          </MenuItem>
          {countries.map((value) => (
            <MenuItem
              dense
              key={value}
              selected={country === value}
              onClick={() => pickCountry(value)}
              sx={monospaceStyle}
            >
              {value}
            </MenuItem>
          ))}
        </Menu>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          color="inherit"
          aria-haspopup="menu"
          aria-expanded={sortMenu !== null}
          onClick={(event) => setSortMenu(event.currentTarget)}
          sx={{
            minWidth: 0,
            px: 0.75,
            textTransform: "none",
            typography: "caption",
            color: "text.disabled",
          }}
        >
          {`sorted by ${sort}`}
          <MenuMark />
        </Button>
        <Menu
          anchorEl={sortMenu}
          open={sortMenu !== null}
          onClose={() => setSortMenu(null)}
        >
          {SORTS.map((value) => (
            <MenuItem
              dense
              key={value}
              selected={sort === value}
              onClick={() => pickSort(value)}
            >
              {value}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No maker on record matches.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label="Makers">
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Proven in</TableCell>
                <TableCell align="right">Products</TableCell>
                <TableCell align="right">Articles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.map((maker) => (
                <TableRow key={maker.organization.id}>
                  <TableCell>
                    <EntityLink
                      id={maker.organization.id}
                      type={maker.organization.type}
                      name={maker.organization.name}
                    />{" "}
                    <Box
                      component="span"
                      sx={{ typography: "caption", color: "text.secondary" }}
                    >
                      {codeWords(maker.role)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {maker.country ? (
                      <Box component="span" sx={monospaceStyle}>
                        {maker.country}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        unknown
                      </Box>
                    )}
                    {/* The production sites of its products, by a pin */}
                    {maker.facilities.map((site) => (
                      <SiteLink key={site.facility.id} site={site} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <ProvenIn maker={maker} pairs={pairs} />
                  </TableCell>
                  <TableCell {...numberCell}>{maker.product_count}</TableCell>
                  <TableCell
                    {...numberCell}
                    sx={{ ...numberCell.sx, color: "text.secondary" }}
                  >
                    {maker.proven_by ? maker.proven_by.articles : NOTHING}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {pages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={monospaceStyle}>
              {`${first + 1}–${first + page.length}`}
            </Box>
            {" of "}
            <Box component="span" sx={monospaceStyle}>
              {shown.length}
            </Box>
          </Typography>
          <Pagination
            size="small"
            count={pages}
            page={at + 1}
            aria-label="Pages of makers"
            onChange={(_event, value: number) => setPageIndex(value - 1)}
          />
        </Box>
      )}

      {/* The step into comparing the makers side by side */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          px: 1.75,
          py: 1.5,
          backgroundColor: "background.raised",
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {`Compare the makers of this ${type}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {`Capability claims with their grade and the specifications the products state, side by side. Opens the benchmark scoped to this ${type}.`}
        </Typography>
        <Link
          component={RouterLink}
          to={exploreAddress(categoryId)}
          underline="hover"
          variant="caption"
          sx={{ alignSelf: "flex-start" }}
        >
          Open comparison
          <Box component="span" aria-hidden>
            {" ›"}
          </Box>
        </Link>
      </Box>
    </Box>
  );
};

export default KindMakers;
